import { useState, type ChangeEvent, type FormEvent } from "react";
import { FileUpload } from "../../shared/FileUpload";
import type { QmForm } from "./state";

interface Props {
  form: QmForm;
  /** File handle for the optional logo upload — held in component state, not localStorage. */
  logoFile: File | null;
  onChange: (patch: Partial<QmForm>) => void;
  onLogoChange: (file: File | null) => void;
  onNext: () => void;
}

const SECTIONS: { id: keyof QmForm; label: string; placeholder: string; uploadHint: string }[] = [
  {
    id: "cvs",
    label: "Lebensläufe / CVs",
    placeholder: "Lebenslauf hier einfügen oder Datei hochladen…",
    uploadHint: "oder Datei hochladen (PDF, DOCX, TXT) — Konvertierung im Hintergrund",
  },
  {
    id: "companyProfile",
    label: "Unternehmensprofil / Firmenbeschreibung",
    placeholder: "Unternehmensbeschreibung, Website-Text oder andere Firmendokumente…",
    uploadHint: "oder Datei hochladen (PDF, DOCX, TXT) — Konvertierung im Hintergrund",
  },
  {
    id: "certificates",
    label: "Zertifikate und Nachweise",
    placeholder: "Zertifikate, Akkreditierungen, Auszeichnungen…",
    uploadHint: "oder Datei hochladen (PDF, DOCX, TXT) — Konvertierung im Hintergrund",
  },
  {
    id: "miscDocs",
    label: "Sonstige Dokumente und Notizen",
    placeholder: "Weitere relevante Informationen, Notizen, Referenzprojekte…",
    uploadHint: "oder Datei hochladen (PDF, DOCX, TXT) — Konvertierung im Hintergrund",
  },
];

export function Step1Uploads({ form, logoFile, onChange, onLogoChange, onNext }: Props) {
  const [logoStatus, setLogoStatus] = useState<string>(logoFile ? `Ausgewählt: ${logoFile.name}` : "");

  function appendToField(field: keyof QmForm, md: string, fileName: string) {
    const current = (form[field] as string) ?? "";
    const prefix = current.trim() ? "\n\n---\n\n" : "";
    const header = `<!-- Aus: ${fileName} -->\n\n`;
    onChange({ [field]: current + prefix + header + md } as Partial<QmForm>);
  }

  function handleLogoSelected(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    onLogoChange(file);
    setLogoStatus(file ? `Ausgewählt: ${file.name}` : "");
  }

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    onNext();
  }

  return (
    <section className="wizard-section" data-qm-step="1">
      <h2 className="form-title">QM Handbuch: Dokumente hochladen</h2>
      <p className="form-description">
        Lade optional Lebensläufe, Unternehmensprofil, Zertifikate oder andere Dokumente hoch.
        Du kannst diesen Schritt auch überspringen und alle Daten manuell eingeben.
      </p>
      <form className="report-form" onSubmit={submit} noValidate>
        <div className="form-row">
          <label htmlFor="qm-website-url">
            Website-URL <span className="field-hint">(optional)</span>
          </label>
          <input
            id="qm-website-url"
            type="url"
            placeholder="https://…"
            value={form.websiteUrl}
            onChange={(e) => onChange({ websiteUrl: e.target.value })}
          />
        </div>

        <div className="form-row">
          <label htmlFor="qm-logo-upload">
            Firmenlogo (optional, PNG/JPG – erscheint oben rechts im PDF)
          </label>
          <div className="upload-area">
            <input
              id="qm-logo-upload"
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/webp"
              onChange={handleLogoSelected}
              aria-label="Logo hochladen"
            />
            <span className="upload-hint">PNG oder JPG hochladen</span>
          </div>
          {logoStatus && <span className="upload-status">{logoStatus}</span>}
        </div>

        {SECTIONS.map((s) => (
          <div className="form-row form-row-full" key={s.id as string}>
            <label htmlFor={`qm-${s.id as string}`}>{s.label}</label>
            <textarea
              id={`qm-${s.id as string}`}
              rows={3}
              placeholder={s.placeholder}
              value={form[s.id] as string}
              onChange={(e) => onChange({ [s.id]: e.target.value } as Partial<QmForm>)}
            />
            <FileUpload
              hint={s.uploadHint}
              onConverted={(md, name) => appendToField(s.id, md, name)}
            />
          </div>
        ))}

        <div className="form-actions">
          <button type="submit" className="btn btn-primary">Weiter zum Formular</button>
          <button type="button" className="btn btn-secondary" onClick={onNext}>
            Überspringen
          </button>
        </div>
      </form>
    </section>
  );
}
